import { getBackendUrl } from "@/lib/backend";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import axios from "@/lib/api";
import { toast } from "sonner";
import { ADMIN_DASHBOARD_ROUTE } from "@/lib/admin";
import { formatApiError } from "@/lib/apiError";

const API = `${getBackendUrl()}/api/admin/auth`;

/* Two-step admin sign-in:
     1. email + password  -> challenge_token (+ TOTP enrollment on first login)
     2. TOTP (6-digit) OR email OTP -> admin JWT cookie
*/

export default function AdminLogin() {
  const [step, setStep] = useState("password"); // "password" | "enroll" | "mfa"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [enrollment, setEnrollment] = useState(null); // {secret, provisioning_uri, issuer}
  const [enrollmentToken, setEnrollmentToken] = useState("");
  const [enrollmentNotice, setEnrollmentNotice] = useState(""); // e.g. expired, used
  const [totpCode, setTotpCode] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [method, setMethod] = useState("totp"); // "totp" | "email"
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${getBackendUrl()}/api/admin/session`)
      .then(() => navigate(ADMIN_DASHBOARD_ROUTE))
      .catch(() => {
        try { localStorage.removeItem("facetra_admin_token"); } catch (e) { void e; }
      });
  }, [navigate]);

  // Shared body for password submit + enrollment-token submit
  const doPasswordCall = async (extraBody = {}) => {
    const { data } = await axios.post(`${API}/password`, {
      email, password, ...extraBody,
    });
    // Case A: server asks for enrollment token (first-time enrollment).
    if (data?.mfa?.enrollment_required && !data.challenge_token) {
      let notice = "";
      if (data.mfa.enrollment_used) notice = "This enrollment code has already been used. Ask an owner to reissue.";
      else if (data.mfa.enrollment_expired) notice = "This enrollment code has expired. Ask an owner to reissue.";
      setEnrollmentNotice(notice);
      setStep("enroll");
      return;
    }
    // Case B: normal challenge (either already-enrolled OR fresh enrollment now returned)
    setChallenge(data.challenge_token);
    const en = data?.mfa?.enrollment;
    if (en) { setEnrollment(en); setMethod("totp"); } else { setEnrollment(null); }
    setStep("mfa");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await doPasswordCall();
    } catch (err) {
      toast.error(formatApiError(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  const submitEnrollmentToken = async (e) => {
    e.preventDefault();
    if (!enrollmentToken.trim()) return;
    setLoading(true);
    try {
      await doPasswordCall({ enrollment_token: enrollmentToken.trim() });
    } catch (err) {
      toast.error(formatApiError(err, "Invalid enrollment code"));
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) return;
    setLoading(true);
    try {
      await axios.post(`${API}/totp/verify`, { challenge_token: challenge, code: totpCode });
      toast.success("Welcome back.");
      navigate(ADMIN_DASHBOARD_ROUTE);
    } catch (err) {
      toast.error(formatApiError(err, "Invalid code"));
    } finally {
      setLoading(false);
    }
  };

  const requestEmailOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/email-otp/request`, { challenge_token: challenge });
      setOtpRequested(true);
      toast.success("Code sent — check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to send code"));
    } finally {
      setLoading(false);
    }
  };

  const submitEmailOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) return;
    setLoading(true);
    try {
      await axios.post(`${API}/email-otp/verify`, { challenge_token: challenge, code: otpCode });
      toast.success("Welcome back.");
      navigate(ADMIN_DASHBOARD_ROUTE);
    } catch (err) {
      toast.error(formatApiError(err, "Invalid code"));
    } finally {
      setLoading(false);
    }
  };

  const resetToPassword = () => {
    setStep("password");
    setChallenge(null);
    setEnrollment(null);
    setTotpCode("");
    setOtpCode("");
    setOtpRequested(false);
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 radial-gold-glow opacity-60" />
      <div className="absolute inset-0 noise-overlay" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="relative w-full max-w-md glass-panel rounded-3xl p-9"
      >
        <Link to="/" data-testid="admin-back-home" className="text-xs text-white/40 hover:text-white/80 transition inline-flex items-center gap-1.5 mb-6">
          ← Back to home
        </Link>
        <div className="flex items-center gap-2.5 mb-7">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_24px_rgba(212,175,55,0.35)]">
            <span className="font-display text-black text-base font-bold">F</span>
          </div>
          <span className="font-display text-2xl text-white tracking-tight">Facetra · Admin</span>
        </div>

        <AnimatePresence mode="wait">
          {step === "password" && (
            <motion.div
              key="pw"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
            >
              <h1 className="font-display text-3xl md:text-4xl text-white tracking-tight">Sign in</h1>
              <p className="mt-2 text-sm text-white/55">Enter your admin credentials. You&apos;ll confirm with 2-factor next.</p>

              <form onSubmit={submitPassword} className="mt-8 space-y-4" autoComplete="off">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block">Email</label>
                  <input
                    type="email"
                    name="facetra-admin-email"
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="admin-email-input"
                    className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/25 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                    placeholder="you@atelier.com"
                    autoFocus
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block">Password</label>
                  <input
                    type="password"
                    name="facetra-admin-pw"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-password-input"
                    className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/25 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition tracking-widest"
                    placeholder="••••••••"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="admin-password-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition-all duration-300 disabled:opacity-60 shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
                >
                  {loading ? "Verifying…" : "Continue →"}
                </button>
              </form>
            </motion.div>
          )}

          {step === "enroll" && (
            <motion.div
              key="enroll"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
            >
              <h1 className="font-display text-3xl md:text-4xl text-white tracking-tight">First-time setup</h1>
              <p className="mt-2 text-sm text-white/55">
                Enter the <strong className="text-[#D4AF37]">one-time enrollment code</strong> from your invite email to activate two-factor authentication.
              </p>
              {enrollmentNotice && (
                <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs p-3">
                  {enrollmentNotice}
                </div>
              )}
              <form onSubmit={submitEnrollmentToken} className="mt-6 space-y-4" data-testid="admin-enroll-form">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Enrollment code</label>
                  <input
                    type="text"
                    value={enrollmentToken}
                    onChange={(e) => setEnrollmentToken(e.target.value)}
                    placeholder="Paste the code from your invite email"
                    data-testid="admin-enroll-token-input"
                    className="mt-2 w-full bg-black/60 border border-white/12 focus:border-[#D4AF37]/60 rounded-lg px-3 py-2.5 text-sm text-white font-mono focus:outline-none"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !enrollmentToken.trim()}
                  data-testid="admin-enroll-submit"
                  className="w-full rounded-full bg-gradient-to-r from-[#F3CE5A] to-[#D4AF37] disabled:opacity-40 text-black font-medium py-3 text-sm tracking-wide transition-all shadow-[0_0_20px_rgba(212,175,55,0.28)]"
                >
                  {loading ? "Verifying…" : "Continue →"}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("password"); setEnrollmentToken(""); setEnrollmentNotice(""); }}
                  className="w-full text-xs text-white/45 hover:text-white/80 transition"
                >
                  ← Back
                </button>
              </form>
            </motion.div>
          )}

          {step === "mfa" && (
            <motion.div
              key="mfa"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
            >
              <h1 className="font-display text-3xl md:text-4xl text-white tracking-tight">Two-factor</h1>
              <p className="mt-2 text-sm text-white/55">
                {enrollment
                  ? "Add Facetra to your authenticator app, then enter the 6-digit code."
                  : "Enter the code from your authenticator app or request an email code."}
              </p>

              {enrollment && (
                <div className="mt-5 p-4 rounded-xl bg-black/40 border border-[#D4AF37]/25">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/85 mb-3">First-time setup</div>
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-3 bg-white rounded-lg">
                      <QRCodeSVG value={enrollment.provisioning_uri} size={148} level="M" />
                    </div>
                    <div className="text-[11px] text-white/50 text-center break-all font-mono">
                      Secret: {enrollment.secret}
                    </div>
                    <div className="text-xs text-white/50 text-center">
                      Scan with Google Authenticator, 1Password, Authy, etc.
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  data-testid="admin-mfa-method-totp"
                  onClick={() => setMethod("totp")}
                  className={`flex-1 py-2 rounded-full text-xs font-medium transition ${
                    method === "totp"
                      ? "bg-[#D4AF37] text-black"
                      : "bg-white/5 text-white/60 hover:bg-white/10"
                  }`}
                >
                  Authenticator
                </button>
                <button
                  type="button"
                  data-testid="admin-mfa-method-email"
                  disabled={!!enrollment}
                  onClick={() => setMethod("email")}
                  className={`flex-1 py-2 rounded-full text-xs font-medium transition ${
                    method === "email"
                      ? "bg-[#D4AF37] text-black"
                      : "bg-white/5 text-white/60 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                  }`}
                >
                  Email code
                </button>
              </div>

              {method === "totp" ? (
                <form onSubmit={submitTotp} className="mt-5 space-y-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block">6-digit code</label>
                    <input
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                      data-testid="admin-totp-input"
                      className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-[0.5em] placeholder-white/20 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                      placeholder="000000"
                      autoFocus
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || totpCode.length !== 6}
                    data-testid="admin-totp-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition-all duration-300 disabled:opacity-60 shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
                  >
                    {loading ? "Verifying…" : "Verify →"}
                  </button>
                </form>
              ) : (
                <div className="mt-5 space-y-4">
                  {!otpRequested ? (
                    <button
                      type="button"
                      onClick={requestEmailOtp}
                      disabled={loading}
                      data-testid="admin-email-otp-request"
                      className="w-full rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium py-3 hover:bg-white/10 transition disabled:opacity-60"
                    >
                      {loading ? "Sending…" : `Email a code to ${email}`}
                    </button>
                  ) : (
                    <form onSubmit={submitEmailOtp} className="space-y-4">
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block">Code from email</label>
                        <input
                          inputMode="numeric"
                          pattern="\d{6}"
                          maxLength={6}
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                          data-testid="admin-email-otp-input"
                          className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-[0.5em] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                          placeholder="000000"
                          autoFocus
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading || otpCode.length !== 6}
                        data-testid="admin-email-otp-submit"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition-all duration-300 disabled:opacity-60 shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
                      >
                        {loading ? "Verifying…" : "Verify →"}
                      </button>
                      <button
                        type="button"
                        onClick={requestEmailOtp}
                        className="w-full text-xs text-white/40 hover:text-white/70 transition"
                      >
                        Resend code
                      </button>
                    </form>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={resetToPassword}
                className="mt-6 w-full text-xs text-white/40 hover:text-white/70 transition"
                data-testid="admin-mfa-back"
              >
                ← Sign in as a different admin
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
